import * as React from 'react';
import {classNames} from '@shopify/react-utilities/styles';
import {capitalizeString} from 'utilities';
import {Text, TextProps} from '../Text';
import {Omit} from '../../../helpers/typeHelpers';
import styles from './Section.scss';

type permittedHeadingTypes = 'h1' | 'h2' | 'h5';

interface HeadingProps
  extends Omit<
    TextProps,
    'inheritSize' | 'secondary' | 'emphasis' | 'text' | 'ellipsis' | 'style'
  > {
  type?: permittedHeadingTypes;
}

interface SubheadProps
  extends Omit<
    TextProps,
    'inheritSize' | 'secondary' | 'emphasis' | 'type' | 'text' | 'style' | 'ellipsis'
  > {}

type SectionElementProps = JSX.IntrinsicElements['section'];

interface SectionProps extends SectionElementProps {
  heading?: string;
  subhead?: string | React.ReactElement;
  spacing?: 'large' | 'medium' | 'small';
  headerSpacing?: 'large' | 'medium' | 'small' | 'x-small' | 'none';
  fullWidth?: boolean;
  resetPaddingTop?: boolean;
  resetPaddingBottom?: boolean;
  headingProps?: HeadingProps;
  subheadProps?: SubheadProps;
  className?: string;
  component?: string | React.ComponentClass;
  children?: React.ReactNode;
}

const SUBHEAD_TYPE_MAP = {
  h1: 'subhead',
  h2: 'subhead2',
};

export function Section({
  heading,
  subhead,
  spacing = 'large',
  headerSpacing = 'medium',
  fullWidth = false,
  resetPaddingTop,
  resetPaddingBottom,
  headingProps = {},
  subheadProps = {},
  className,
  component = 'section',
  children,
  ...restProps
}: SectionProps) {
  const {type: headingType = 'h2', component: headingComponent = 'h2'} = headingProps;
  const subheadType = SUBHEAD_TYPE_MAP[headingType] || 'regular';
  const showHeader = Boolean(heading) || Boolean(subhead);
  const Wrapper = component;
  const headerSpacerClass =
    showHeader && Boolean(children)
      ? styles[`HeaderSpacer-${capitalizeString(headerSpacing)}`]
      : undefined;

  return (
    <Wrapper
      className={classNames(className, spacing && styles[capitalizeString(spacing)], {
        [styles.PageContainer]: !fullWidth,
        [styles.PaddingOnlyBottom]: resetPaddingTop,
        [styles.PaddingOnlyTop]: resetPaddingBottom,
        [styles.PaddingTopBottom]: !resetPaddingBottom && !resetPaddingTop,
      })}
      {...restProps}
    >
      {showHeader && (
        <div className={headerSpacerClass}>
          {Boolean(heading) && (
            <Text
              text={heading}
              type={headingType}
              component={headingComponent}
              className={styles[`SectionHeading-${headingType}`]}
              {...headingProps}
            />
          )}
          {Boolean(subhead) && (
            <Text text={subhead} type={subheadType} component="p" resetMargin {...subheadProps} />
          )}
        </div>
      )}
      {children}
    </Wrapper>
  );
}
